/* eslint-disable import/no-duplicates */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../../services/api';

import {
  formatPrice,
  PERMISSAO_INDIVIDUAL,
  TIPO_USUARIO,
} from '../../../../utils/funcoes';

import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';
import { useClinica } from '../../../../hooks/clinica';

import Input from '../../../../components/Input';
import Loading from '../../../../components/Loading';

import getValidationErrors from '../../../../utils/getValidationErrors';

import { RelatorioHeader } from './styles';
import getResponseErrors from '../../../../utils/getResponseErrors';

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}
interface ICobranca {
  CprDataPag: Date;
  dataPagFormatada: string;
  PacNome: string;
  CprHistorico: string;
  CprValorPag: number;
  CobData: Date;
  dataCobrancaFormatada: string;
  PlaNumero: string;
  Dias: number;
  valorFormatado: string;
}
interface IRetorno {
  cobrancas: ICobranca[];
  Total: number;
  count: number;
}

const Cobrancas31: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();

  const permissoes = useMemo(() => {
    return user.UsuPermissao.split(',');
  }, [user.UsuPermissao]);

  useEffect(() => {
    if (
      user.UsuTipo > TIPO_USUARIO.ATENDENTE_CAIXA &&
      user.UsuTipo !== TIPO_USUARIO.COBRANCA &&
      !permissoes.some(
        permissao =>
          permissao === PERMISSAO_INDIVIDUAL.COBRANCAS_RECEBIMENTO_31,
      )
    ) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const { addToast } = useToast();
  const { imagensClinica } = useClinica();

  const logo =
    imagensClinica.find(img => img.nome === 'logoRelatorio')?.arquivo_url || '';

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [tabelaRecebidos, setTabelaRecebidos] = useState<ICobranca[]>([]);
  const [total, setTotal] = useState('R$ 0,00');
  const [count, setCount] = useState(0);

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  const buscaDados = useCallback(
    async (DataIni: Date, DataFim: Date) => {
      setLoading(true);
      try {
        const dataInicioSelecionada = parseISO(DataIni.toString());
        const dataFimSelecionada = parseISO(DataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const response = await api.get<IRetorno>(
          `/cobranca/recebidos31?DataIni=${dataInicioFormatada}&DataFim=${dataFimFormatada}`,
        );

        const cobrancas = response.data.cobrancas.map(item => ({
          ...item,
          valorFormatado: formatPrice(item.CprValorPag),
          dataCobrancaFormatada: format(
            parseISO(item.CobData.toString()),
            'dd/MM/yyyy',
          ),
          dataPagFormatada: format(
            parseISO(item.CprDataPag.toString()),
            'dd/MM/yyyy',
          ),
          Dias: -1 * item.Dias,
        }));

        setTabelaRecebidos(cobrancas);
        if (response.data.Total) setTotal(formatPrice(response.data.Total));

        if (response.data.count) setCount(response.data.count);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados de cobrança',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });
        await buscaDados(dados.dataInicio, dados.dataFim);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        }
      } finally {
        setLoading(false);
      }
    },
    [buscaDados],
  );

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2">
            <h2 id="topoBusca">
              Valores recebidos com mais de 31 dias de atraso
            </h2>
          </div>

          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
          >
            <Row>
              <Col md={2}>
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2}>
                <Input name="dataFim" label="Até" type="date" required />
              </Col>

              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <div className="bg-white border p-2 my-3">
          <img src={logo} alt="Agenda facil" /> Impresso em{' '}
          {dataExibe?.dataHora}
        </div>

        <RelatorioHeader>
          VALORES RECEBIDOS {dataExibe?.dataDiaSemana}
        </RelatorioHeader>
        <div className="bg-white border p-2">
          {count > 0 && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '100px' }}>Data Pag</th>
                    <th style={{ width: '70px' }}>Cartão</th>
                    <th>Paciente</th>
                    <th>Histórico</th>
                    <th style={{ width: '70px' }}>Dias</th>
                    <th style={{ width: '100px' }}>Valor</th>
                    <th style={{ width: '100px' }}>Dt. Cobrança</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={2}>Total: {total}</td>
                    <td colSpan={5}>Nº Registros: {count}</td>
                  </tr>
                  {tabelaRecebidos.map(item => (
                    <tr
                      key={`recebidos-${item.PlaNumero}`}
                      style={{ fontSize: '.7rem', height: '30px' }}
                    >
                      <td>{item.dataPagFormatada}</td>
                      <td>{item.PlaNumero}</td>
                      <td>{item.PacNome}</td>
                      <td>{item.CprHistorico}</td>
                      <td>{item.Dias}</td>
                      <td>{item.valorFormatado}</td>
                      <td>{item.dataCobrancaFormatada}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {(!count || count === 0) && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '250px' }}
            >
              <h5>Nenhum registro encontrado</h5>
            </div>
          )}
        </div>
      </>
    </Container>
  );
};

export default Cobrancas31;
